import React from "react";
import { Box, Container, Typography, Stack, Grid } from "@mui/material";
import { motion } from "framer-motion";
import icon1 from "../../Assets/Icons/1.svg";
import icon2 from "../../Assets/Icons/2.svg";
import icon3 from "../../Assets/Icons/3.svg";
import rightSideImage from "../../Assets/Background-For the Beyond Construction.svg";

const WhyChooseAbilioSection = () => {
  const benefits = [
    {
      icon: icon1,
      title: "Uncompromising Aesthetics",
      description:
        "Every design choice is made with a focus on timeless elegance and refined luxury.",
    },
    {
      icon: icon2,
      title: "Structural Reliability",
      description:
        "Our infrastructure wing ensures every project meets the highest global safety and quality standards.",
    },
    {
      icon: icon3,
      title: "Bespoke Solutions",
      description:
        "Tailored strategies for interior design and real estate that align perfectly with your unique lifestyle.",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const slideUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const benefitVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const floatingCardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#0A0908",
        pt: { xs: "64px", lg: "128px" },
        pb: { xs: "64px", lg: "128px" },
        height: { xs: "auto", lg: "550px" },
        opacity: 1,
        transform: "rotate(0deg)",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1280px",
          px: "32px",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "flex-start",
          gap: { xs: "64px", lg: "80px" },
        }}
      >
        {/* Left Content Box */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          style={{
            width: "100%",
            maxWidth: "568px",
            display: "flex",
            flexDirection: "column",
            gap: "32px",
          }}
        >
          {/* Header */}
          <motion.div variants={slideUpVariants}>
            <Box>
              <Typography
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 800,
                  fontSize: { xs: "32px", sm: "40px", lg: "48px" },
                  lineHeight: { xs: "40px", sm: "48px", lg: "56px" },
                  letterSpacing: "-2.4px",
                  color: "#FFFFFF",
                }}
              >
                Beyond Construction.
                <br />
                <Box component="span" sx={{ color: "#FF5722" }}>
                  Total Transformation.
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "26px",
                  color: "#57534E",
                  mt: "24px",
                  maxWidth: "448px",
                }}
              >
                We don't just build structures; we curate environments that
                inspire the way people live and work.
              </Typography>
            </Box>
          </motion.div>

          {/* Benefits List */}
          <Stack spacing="32px">
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={benefitVariants}>
                <Box
                  sx={{
                    width: { xs: "100%", lg: "477.17px" },
                    height: { xs: "auto", lg: "84px" },
                    display: "flex",
                    gap: "16px",
                    opacity: 1,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateX(8px)",
                    },
                  }}
                >
                  {/* Icon Box */}
                  <Box
                    sx={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#B02F001A",
                      borderRadius: "8px",
                      border: "1px solid #B02F0033",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#B02F0033",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <img
                      src={benefit.icon}
                      alt={benefit.title}
                      style={{
                        width: "24px",
                        height: "24px",
                        objectFit: "contain",
                      }}
                    />
                  </Box>

                  {/* Content side */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      textAlign: "left",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 700,
                        fontSize: "18px",
                        lineHeight: "28px",
                        color: "#FFFFFF",
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "22px",
                        color: "#57534E",
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Stack>
        </motion.div>

        {/* Right Image Box */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={imageVariants}
          style={{
            width: "100%",
            maxWidth: "568px",
            height: "100%",
            minHeight: "350px",
            position: "relative",
          }}
        >
          {/* Main Image */}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            <motion.img
              src={rightSideImage}
              alt="Structural details"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </Box>

          {/* Floating Experience Card */}
          <motion.div
            variants={floatingCardVariants}
            style={{
              position: "absolute",
              bottom: "32px",
              left: "0px",
            }}
          >
            <Box
              sx={{
                width: { xs: "140px", sm: "160px", lg: "200px" },
                height: { xs: "auto", lg: "60px" },
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: { xs: "16px 20px", lg: "32px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "4px",
                boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1)",
                zIndex: 2,
                ml: { xs: "20px", lg: "-102px" },
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.02)",
                  boxShadow: "0px 20px 30px -5px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 800,
                  fontSize: { xs: "28px", lg: "36px" },
                  lineHeight: "1",
                  color: "#1C1917",
                }}
              >
                8+
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: { xs: "10px", lg: "12px" },
                  letterSpacing: "0.05em",
                  color: "#57534E",
                  textTransform: "uppercase",
                }}
              >
                Years Excellence
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default WhyChooseAbilioSection;
